import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChannelBadge } from "@/components/ChannelBadge";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { useWorkGuard } from "@/context/WorkGuard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtTL } from "@/components/StatusBadge";
import { curSym } from "@/lib/company";
import { productImage } from "@/lib/images";
import BarcodeScanner from "@/components/BarcodeScanner";
import useScannerWedge from "@/lib/useScannerWedge";
import { deviceFlag } from "@/lib/device";
import { loadDevicePolicy, policyFlag } from "@/lib/devicePolicy";
import PaymentDialog from "@/components/PaymentDialog";
import DiscountAuthDialog from "@/components/DiscountAuthDialog";
import ApprovalDialog from "@/components/ApprovalDialog";
import WholesaleAuthDialog from "@/components/WholesaleAuthDialog";
import CustomerSearchSelect from "@/components/CustomerSearchSelect";
import { ThermalDoc } from "@/components/ThermalReceipt";
import { InvoiceA5Doc, printInvoiceNode } from "@/components/InvoiceA5";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { newTxId, nextOfflineNo, enqueueSale, getQueue, queueCount, removeSale, isNetworkError, logSync, getSyncLog, QUEUE_STORAGE_ERROR } from "@/lib/offlineQueue";
import { cacheCatalog, getCachedCatalog, filterCatalog, findByBarcode } from "@/lib/offlineCache";
import { cdPost, cdSetKey } from "@/lib/customerDisplay";
import { useScale, scaleSupported, connectScale, disconnectScale, autoConnectScale } from "@/lib/scale";
import {
  ShoppingCart, Search, Plus, Minus, Trash2, Check, ScanLine, X,
  LockKeyhole, DoorOpen, DoorClosed, Wallet, Percent, Ticket, Clock, User, Printer, Loader2,
  WifiOff, UploadCloud, RefreshCw, History, Monitor, Scale, Tag, KeyRound, CreditCard, Lock,
} from "lucide-react";

const uid = () => `c${Date.now()}${Math.floor(Math.random() * 1000)}`;
const makeCart = (slot, channel = "market") => ({ cart_id: uid(), slot, label: `Sepet ${slot}`, channel, customer_id: null, discount: 0, lines: [] });
const nextSlot = (carts) => { const used = new Set(carts.map((c) => c.slot)); let n = 1; while (used.has(n)) n += 1; return n; };
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const EMPTY_LOCATIONS = [];
const EMPTY_LINES = [];
const round3 = (n) => Math.round((n + Number.EPSILON) * 1000) / 1000;
const isWeighed = (u) => { const s = String(u || "").trim().toLowerCase(); return ["kg", "kilo", "kilogram", "gram", "gr"].includes(s); };

function custPct(cust, category) {
  if (!cust) return 0;
  for (const r of cust.category_discounts || []) {
    const c = (r.category || "").trim();
    if (c && c !== "*" && category && c === category) return Number(r.discount_pct) || 0;
  }
  return Number(cust.default_discount_pct) || 0;
}

export default function POS() {
  const { can, isPrivileged, user, feature } = useAuth();
  const { activeLocId, setActive } = useActiveLocation();
  const { setWork } = useWorkGuard();
  const [shift, setShift] = useState(null);
  const [shiftReport, setShiftReport] = useState(null);
  const [shiftLoaded, setShiftLoaded] = useState(false);
  const [shiftReminderDismissed, setShiftReminderDismissed] = useState(false);
  const [myShiftsOpen, setMyShiftsOpen] = useState(false);
  const [myShifts, setMyShifts] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [carts, setCarts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [openAmount, setOpenAmount] = useState("");
  const [openPin, setOpenPin] = useState("");
  const [openLoc, setOpenLoc] = useState("");
  const [openReg, setOpenReg] = useState("");
  const [openOperatorPin, setOpenOperatorPin] = useState("");   // Kimlik Modeli: ortak girişte vardiyayı açan personel PIN'i
  const [closeOperatorPin, setCloseOperatorPin] = useState("");
  const [registers, setRegisters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [openingBusy, setOpeningBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePin, setClosePin] = useState("");
  const [showSupApproval, setShowSupApproval] = useState(false);
  const [supMethod, setSupMethod] = useState("pin");
  const [countedCash, setCountedCash] = useState("0");
  const [whCust, setWhCust] = useState(null);
  const [supCred, setSupCred] = useState({ pin: "", card: "", email: "", password: "" });
  const [closing, setClosing] = useState(false);
  const [zReport, setZReport] = useState(null);
  const [printDoc, setPrintDoc] = useState(null);
  const [invoiceDoc, setInvoiceDoc] = useState(null);
  const [weighOpen, setWeighOpen] = useState(false);
  const [weighProduct, setWeighProduct] = useState(null);
  const [weighVal, setWeighVal] = useState("");
  const scale = useScale();
  useEffect(() => { if (scaleSupported()) autoConnectScale(deviceFlag("scaleBaud", 9600)); }, []);
  useEffect(() => { loadDevicePolicy(); }, []);
  useEffect(() => {
    // Yalnızca terazide GERÇEK ağırlık varsa canlı doldur; teraziye bir şey KONMAMIŞKEN
    // (ağırlık ~0) kasiyerin elle girdiği değeri ASLA silme — "efsane" davranış.
    if (weighOpen && scale.connected && scale.weight > 0.005) setWeighVal(String(Math.round(scale.weight * 1000) / 1000));
  }, [weighOpen, scale.connected, scale.weight]);
  const scaleActive = scale.connected && scale.weight > 0.005; // teraziye bir şey konulduğunda aktif (elle giriş kilitlenir)
  const [mealCards, setMealCards] = useState({ enabled: false, providers: [] });
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [posPage, setPosPage] = useState(1);
  const [perPage, setPerPage] = useState(30);
  const gridRef = useRef(null);
  const cartListRef = useRef(null);
  const [selectedCat, setSelectedCat] = useState("");
  const [allCats, setAllCats] = useState([]);
  const [custMap, setCustMap] = useState({});
  const [scanOpen, setScanOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payInitial, setPayInitial] = useState([]);
  const [campPrev, setCampPrev] = useState({ campaign_discount: 0, campaigns_applied: [] });
  const [loyInfo, setLoyInfo] = useState(null);
  const [redeemPts, setRedeemPts] = useState(0);
  const [approveOpen, setApproveOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [discOpen, setDiscOpen] = useState(false);
  const [discTokens, setDiscTokens] = useState({});
  const [discPolicy, setDiscPolicy] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [queueError, setQueueError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [offlineSince, setOfflineSince] = useState(null);
  const [, setTick] = useState(0);
  const [syncLog, setSyncLog] = useState([]);
  const [syncLogOpen, setSyncLogOpen] = useState(false);
  const [qtyMult, setQtyMult] = useState(1);
  const qtyMultRef = useRef(1);
  useEffect(() => { qtyMultRef.current = qtyMult; }, [qtyMult]);
  const resetMult = () => setQtyMult(1);

  const readyRef = useRef(false);

  const active = carts.find((c) => c.cart_id === activeId) || carts[0] || null;
  const channel = (active?.customer_id && active?.wholesale_auth) ? "toptan" : "market";
  const myLocs = user?.locations || EMPTY_LOCATIONS;
  const hasShift = Boolean(shift);
  const needLoc = isPrivileged || myLocs.length > 1;
  const locOpts = isPrivileged ? locations.filter((l) => l.is_active !== false) : myLocs;

  const loadShift = useCallback(() => {
    return api.get("/shifts/current").then((r) => {
      const s = r.data?.shift || null;
      setShift(s);
      setShiftReport(r.data?.report || null);
      if (s) {
        let cs = (s.carts || []).map((c, i) => ({ ...c, lines: c.lines || [], slot: c.slot || (parseInt(String(c.label || "").replace(/\D/g, ""), 10) || i + 1) }));
        cs = cs.map((c) => ({ ...c, label: `Sepet ${c.slot}` }));
        if (cs.length === 0) cs = [makeCart(1)];
        setCarts(cs);
        const ap = s.active_payment;
        if (ap && ap.cart_id && cs.some((c) => c.cart_id === ap.cart_id && (c.lines || []).length > 0)) {
          setActiveId(ap.cart_id);
          setPayInitial(ap.collected || []);
          setTimeout(() => setPayOpen(true), 250);
        } else {
          setActiveId(s.active_cart_id && cs.some((c) => c.cart_id === s.active_cart_id) ? s.active_cart_id : cs[0].cart_id);
        }
      } else { setCarts([]); setActiveId(null); }
    }).catch(() => {}).finally(() => { setShiftLoaded(true); setTimeout(() => (readyRef.current = true), 300); });
  }, []);
  useEffect(() => { loadShift(); }, [loadShift]);
  const openLocEff = needLoc ? openLoc : (myLocs[0]?.id || "");
  useEffect(() => {
    if (hasShift || !openLocEff) { setRegisters([]); return; }
    api.get(`/registers?location_id=${openLocEff}`)
      .then((r) => { const act = (r.data || []).filter((x) => x.active !== false); setRegisters(act); setOpenReg((cur) => (act.some((x) => x.id === cur) ? cur : (act[0]?.id || ""))); })
      .catch(() => setRegisters([]));
  }, [hasShift, shift?.id, openLocEff]);

  // Vardiya kapanış hatırlatması: kasa 21:00 sonrası VEYA 10+ saattir açıksa (kapatılabilir).
  useEffect(() => {
    if (!shift?.id) { setShiftReminderDismissed(false); return; }
    try { setShiftReminderDismissed(localStorage.getItem("groot_shift_reminder_" + shift.id) === "1"); } catch { /* ignore */ }
  }, [shift?.id]);
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 60000); return () => clearInterval(t); }, []);

  // Devam eden sepetlerdeki müşteri kayıtlarını (id) tekil çöz — 10bin+ müşteride tüm listeyi yüklemeyiz
  useEffect(() => {
    if (!can("customer.read")) return;
    const ids = [...new Set(carts.map((c) => c.customer_id).filter(Boolean))].filter((id) => !custMap[id]);
    if (ids.length === 0) return;
    Promise.all(ids.map((id) => api.get(`/customers/${id}`).then((r) => [id, r.data]).catch(() => null)))
      .then((pairs) => { const add = {}; pairs.forEach((p) => { if (p) add[p[0]] = p[1]; }); if (Object.keys(add).length) setCustMap((m) => ({ ...m, ...add })); });
  }, [carts, custMap, can]);

  useEffect(() => {
    if (isPrivileged) {
      api.get("/locations").then((r) => {
        setLocations(r.data);
        setOpenLoc((c) => c || activeLocId || r.data.find((l) => (l.types || [l.type]).includes("store"))?.id || r.data[0]?.id || "");
      }).catch(() => {});
    } else if (myLocs.length > 1) {
      setOpenLoc((c) => c || activeLocId || myLocs[0]?.id || "");
    }
  }, [isPrivileged, myLocs, activeLocId]);

  const loadPolicy = useCallback(() => { api.get("/pos/discount/policy").then((r) => setDiscPolicy(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  const loadProducts = useCallback(() => {
    // POS'ta yalnız satılabilir (aktif kanalda fiyatlı) ürünler; arama açıkken de kanal filtresi KORUNUR
    // → alakasız/fiyatsız ürün gelmez ("su" yazınca zambak çıkmaz). Sonuçlar backend'de alaka sırasına dizilir.
    const params = new URLSearchParams({ channel, channel_only: "true", page_size: "500" });
    if (q) params.set("q", q);
    if (selectedCat) params.set("category", selectedCat);
    if (shift?.location_id) params.set("location_id", shift.location_id);
    api.get(`/products?${params}`).then((r) => setProducts(r.data.items || [])).catch(async () => {
      // Çevrimdışı: önbellekteki tam katalogda ara
      const cat = await getCachedCatalog(channel);
      if (cat) setProducts(filterCatalog(cat.items, q, { limit: 1000 }));
    });
  }, [channel, q, selectedCat, shift?.location_id]);
  useEffect(() => { if (!shift) return; const t = setTimeout(loadProducts, q ? 250 : 0); return () => clearTimeout(t); }, [loadProducts, shift, q]);
  useEffect(() => { setPosPage(1); }, [q, selectedCat, channel, products.length]);
  useEffect(() => {
    const measure = () => {
      const el = gridRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const avail = window.innerHeight - top - 72;
      const rows = Math.max(6, Math.floor(avail / 128));
      setPerPage((prev) => (prev === rows * 5 ? prev : rows * 5));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [shift, allCats.length]);
  const posTotalPages = Math.max(1, Math.ceil(products.length / perPage));
  const posCurPage = Math.min(posPage, posTotalPages);
  const posPageItems = products.slice((posCurPage - 1) * perPage, posCurPage * perPage);
  const posPageWindow = () => {
    const span = 7;
    let start = Math.max(1, posCurPage - 3);
    const end = Math.min(posTotalPages, start + span - 1);
    start = Math.max(1, end - span + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };
  // Offline için tam katalog önbelleği (kanal bazlı) — internet varken sessizce güncellenir; kategori sekmeleri de buradan.
  // AĞIR istek (2000 ürün): ilk boyamayı bloklamasın diye sayfa etkileşime hazır olduktan SONRA (idle) çalışır → kasa hızlı açılır.
  useEffect(() => {
    if (!hasShift || !online) return;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const params = new URLSearchParams({ channel, channel_only: "true", page_size: "2000" });
      if (shift?.location_id) params.set("location_id", shift.location_id);
      api.get(`/products?${params}`).then((r) => {
        if (cancelled) return;
        const items = r.data.items || [];
        cacheCatalog(channel, items);
        setAllCats([...new Set(items.map((i) => i.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")));
      }).catch(() => {});
    };
    const ric = typeof window !== "undefined" && window.requestIdleCallback;
    const handle = ric ? window.requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 1200);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) { try { window.cancelIdleCallback(handle); } catch { /* ignore */ } }
      else clearTimeout(handle);
    };
  }, [hasShift, shift?.id, shift?.location_id, channel, online]);

  // Persist carts (server-side) — sepet asla kaybolmaz
  useEffect(() => {
    if (!hasShift) return;
    const t = setTimeout(() => {
      if (!readyRef.current) return;
      api.put("/shifts/carts", { carts, active_cart_id: activeId }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [carts, activeId, hasShift, shift?.id]);

  const anyOpen = carts.some((c) => (c.lines || []).length > 0);
  useEffect(() => { setWork("pos", anyOpen); return () => setWork("pos", false); }, [anyOpen, setWork]);

  const patchActive = (fn) => setCarts((cs) => cs.map((c) => (c.cart_id === (activeId || cs[0]?.cart_id) ? fn(c) : c)));
  const priceOf = (p) => (channel === "toptan" ? p.sales_prices?.wholesale_pickup : p.sales_prices?.market_retail) || 0;
  // Sepet satırı iki fiyatı da taşır; gösterilen/tahsil edilen birim fiyat AKTİF kanaldan türetilir
  // (müşteri seçilince toptan, kaldırılınca perakende — bayat fiyat / müşterisiz toptan satış imkânsız).
  const lineUnit = useCallback((x) => { const v = channel === "toptan" ? x.price_wholesale : x.price_market; return (v != null ? v : x.unit_price) || 0; }, [channel]);

  const add = (p, qtyArg) => {
    if (!shift) return toast.error("Önce kasayı açın");
    const weighed = isWeighed(p.base_unit);
    // Kilolu ürüne katlı (2x/3x…) işlem YAPILAMAZ → uyarı ver, çarpanı 1x'e sıfırla, ağırlığı sıfırla, iptal et
    if (weighed && qtyMultRef.current > 1) {
      toast.warning("Kilolu ürünlere katlı işlem yapılamaz");
      resetMult(); setWeighVal("");
      return;
    }
    if (weighed && qtyArg == null) {
      setWeighProduct(p); setWeighVal(""); setWeighOpen(true); return;
    }
    // Adet çarpanı: kasiyer önce adet seçtiyse (örn. 5×), normal tap/okutmada o adet eklenir
    if (qtyArg == null && qtyMultRef.current > 1) {
      const m = qtyMultRef.current;
      addToCart(p, m);
      resetMult();
      return;
    }
    addToCart(p, qtyArg);
  };
  const addToCart = (p, qtyArg) => {
    if (!shift) return toast.error("Önce kasayı açın");
    if (p.stock_on_hand <= 0) return toast.error("Stok yok");
    const weighed = isWeighed(p.base_unit);
    const q = weighed ? round3(qtyArg || 0) : (qtyArg || 1);
    if (weighed && q <= 0) return;
    setLastSale(null);
    patchActive((c) => {
      const ex = c.lines.find((x) => x.product_id === p.id);
      if (ex) {
        const nq = weighed ? round3(ex.qty + q) : ex.qty + q;
        if (nq > p.stock_on_hand) { toast.error("Stok yetersiz"); return c; }
        return { ...c, lines: c.lines.map((x) => (x.product_id === p.id ? { ...x, qty: nq } : x)) };
      }
      if (q > p.stock_on_hand) { toast.error("Stok yetersiz"); return c; }
      return { ...c, lines: [...c.lines, { product_id: p.id, name: p.name, price_market: p.sales_prices?.market_retail || 0, price_wholesale: p.sales_prices?.wholesale_pickup || 0, unit_price: priceOf(p), qty: q, category: p.category, unit: p.base_unit, weighed, stock: p.stock_on_hand }] };
    });
    // Sepet uzadıkça sayfa BAŞTA kalır (grid oynamaz); sadece sepetin kendi listesi en son eklenene kayar
    setTimeout(() => { const el = cartListRef.current; if (el) el.scrollTop = el.scrollHeight; }, 60);
  };
  const confirmWeigh = () => {
    const kg = round3(parseFloat(String(weighVal).replace(",", ".")) || 0);
    if (kg <= 0) return toast.error("Geçerli bir ağırlık girin");
    addToCart(weighProduct, kg);
    setWeighOpen(false); setWeighProduct(null); setWeighVal("");
  };
  const setQtyC = (pid, qty) => patchActive((c) => ({ ...c, lines: c.lines.map((x) => {
    if (x.product_id !== pid) return x;
    const w = isWeighed(x.unit);
    const min = w ? 0.001 : 1;
    let nq = w ? round3(qty) : Math.round(qty);
    nq = Math.max(min, Math.min(nq, x.stock || nq));
    return { ...x, qty: nq };
  }) }));
  const doRemoveItem = (pid) => patchActive((c) => ({ ...c, lines: c.lines.filter((x) => x.product_id !== pid) }));
  const requestRemoveItem = (pid) => { if (can("sale.void.approve")) return doRemoveItem(pid); setPending({ type: "item", pid }); setApproveOpen(true); };

  const setCustomerC = (obj) => {
    const cid = obj?.id || null;
    if (cid && obj) setCustMap((m) => ({ ...m, [cid]: obj }));
    patchActive((c) => ({ ...c, customer_id: cid, wholesale_auth: null }));
    if (obj?.can_wholesale) setWhCust(obj);
  };
  const onWholesaleAuthorized = async (auth) => {
    patchActive((c) => ({ ...c, wholesale_auth: auth }));
    // Devam eden (sunucuda kayıtlı) sepette satır fiyatlarını toptan fiyatla tazele — UI toplamı fiili tahsilatla eşleşsin
    try {
      const params = new URLSearchParams({ channel: "toptan", channel_only: "true", page_size: "500" });
      if (shift?.location_id) params.set("location_id", shift.location_id);
      const { data } = await api.get(`/products?${params}`);
      const map = {};
      (data.items || []).forEach((p) => { map[p.id] = p; });
      patchActive((c) => ({ ...c, lines: c.lines.map((x) => {
        const p = map[x.product_id];
        if (!p) return x;
        const pw = p.sales_prices?.wholesale_pickup;
        const pm = p.sales_prices?.market_retail;
        return { ...x, price_wholesale: pw != null ? pw : x.price_wholesale, price_market: pm != null ? pm : x.price_market };
      }) }));
    } catch (e) { /* sessiz — yeni sepet zaten doğru fiyatlı */ }
  };

  const addCart = () => {
    if (carts.length >= 8) return toast.error("En fazla 8 açık sepet");
    const c = makeCart(nextSlot(carts));
    setCarts((cs) => [...cs, c]); setActiveId(c.cart_id); setLastSale(null);
  };
  const doRemoveCart = (id) => {
    setCarts((cs) => {
      const left = cs.filter((c) => c.cart_id !== id);
      const next = left.length ? left : [makeCart(1)];
      setActiveId((cur) => (cur === id ? next[0].cart_id : cur));
      return next;
    });
  };
  const requestRemoveCart = (id) => {
    const c = carts.find((x) => x.cart_id === id);
    if (c && c.lines.length > 0 && !can("sale.void.approve")) { setPending({ type: "cart", id }); setApproveOpen(true); return; }
    if (c && c.lines.length > 0 && !window.confirm("Bu sepet boşaltılsın mı?")) return;
    doRemoveCart(id);
  };

  const handleScan = async (code) => {
    try {
      const suffix = shift?.location_id ? `?location_id=${shift.location_id}` : "";
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}${suffix}`);
      if (data.scanned_qty != null && data.scanned_qty > 0) {
        add(data, data.scanned_qty);
        toast.success(`Sepete eklendi: ${data.name} · ${data.scanned_qty} ${data.base_unit || "kg"}`);
      } else {
        add(data);
        toast.success(`Sepete eklendi: ${data.name}`);
      }
    }
    catch (err) {
      // Çevrimdışı: barkodu önbellekteki katalogdan bul
      const cat = await getCachedCatalog(channel);
      const p = cat ? findByBarcode(cat.items, code) : null;
      if (p) { add(p); toast.success(`Sepete eklendi (çevrimdışı): ${p.name}`); return; }
      toast.error(`Barkod bulunamadı: ${code}`);
    }
  };
  useScannerWedge({ onScan: handleScan, enabled: true, beep: deviceFlag("scanBeep", true) });

  const cust = custMap[active?.customer_id] || null;
  const lines = active?.lines || EMPTY_LINES;
  const gross = round2(lines.reduce((s, x) => s + lineUnit(x) * x.qty, 0));
  const custDisc = round2(lines.reduce((s, x) => s + lineUnit(x) * x.qty * custPct(cust, x.category) / 100, 0));
  const discountPct = Math.min(Math.max(0, Number(active?.discount) || 0), 100);
  const manualDisc = round2(Math.min(gross * discountPct / 100, Math.max(0, gross - custDisc)));
  const preCouponTotal = round2(Math.max(0, gross - custDisc - manualDisc));
  const couponDisc = couponInfo
    ? round2(couponInfo.discount_type === "amount"
        ? Math.min(Number(couponInfo.discount_value) || 0, preCouponTotal)
        : preCouponTotal * (Number(couponInfo.discount_value) || 0) / 100)
    : 0;
  const campDisc = round2(Math.min(campPrev?.campaign_discount || 0, Math.max(0, preCouponTotal - couponDisc)));
  const loyEnabled = !!loyInfo?.enabled;
  const redeemRate = Number(loyInfo?.redeem_rate) || 0;
  const maxRedeemByBasket = redeemRate > 0 ? Math.floor(Math.max(0, preCouponTotal - couponDisc - campDisc) / redeemRate) : 0;
  const redeemPtsEff = loyEnabled && active?.customer_id ? Math.min(Number(redeemPts) || 0, Number(loyInfo?.points_balance) || 0, maxRedeemByBasket) : 0;
  const redeemVal = round2(redeemPtsEff * redeemRate);
  const total = round2(Math.max(0, preCouponTotal - couponDisc - campDisc - redeemVal));
  const activeToken = active ? discTokens[active.cart_id] : null;

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal: preCouponTotal });
      setCouponInfo(data);
      toast.success(`Kupon uygulandı: −${fmtTL(data.discount)}`);
    } catch (err) { setCouponInfo(null); toast.error(apiError(err.response?.data?.detail)); }
    finally { setCouponBusy(false); }
  };
  const clearCoupon = () => { setCouponInfo(null); setCouponCode(""); };

  // Kampanya + sadakat önizleme (POS'ta otomatik uygulanan indirimleri ve kazanılacak puanı göster)
  useEffect(() => {
    if (payOpen) return;
    if (!lines.length) {
      // Boş sepet: idempotent sıfırla — değer zaten sıfırsa AYNI referansı döndür ki
      // (active=null iken lines=[] her render yeni referans olsa bile) sonsuz render döngüsü olmasın.
      setCampPrev((p) => ((p?.campaign_discount === 0 && (p?.campaigns_applied?.length ?? 0) === 0) ? p : { campaign_discount: 0, campaigns_applied: [] }));
      setLoyInfo((p) => (p === null ? p : null));
      return;
    }
    const body = {
      lines: lines.map((x) => ({ product_id: x.product_id, quantity: x.qty, unit_price: lineUnit(x), line_total: round2(x.qty * lineUnit(x)), category: x.category })),
      gross_total: gross, customer_id: active?.customer_id || null,
      other_discount: round2(custDisc + manualDisc + couponDisc),
    };
    const t = setTimeout(() => {
      api.post("/loyalty/preview", body)
        .then((r) => { setCampPrev({ campaign_discount: r.data.campaign_discount || 0, campaigns_applied: r.data.campaigns_applied || [] }); setLoyInfo(r.data.loyalty || null); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [lines, gross, custDisc, manualDisc, couponDisc, active?.customer_id, payOpen, lineUnit]);

  // Müşteri ekranı (ikinci monitör) — sepet anlık yansır; ödeme sırasında PaymentDialog yönetir
  useEffect(() => { cdSetKey(shift?.id || null); }, [shift?.id]);
  useEffect(() => {
    if (payOpen) return;
    const store = shift?.location_name || "Groot Market";
    if (!lines.length) { cdPost({ mode: "idle", store }); return; }
    cdPost({
      mode: "cart", store, customer: cust?.name || null,
      lines: lines.map((x) => ({ name: x.name, qty: x.qty, unit: x.unit, unit_price: lineUnit(x), line_total: round2(x.qty * lineUnit(x)) })),
      gross, discount: round2(custDisc + manualDisc), total,
    });
  }, [lines, gross, custDisc, manualDisc, total, payOpen, shift?.id, shift?.location_name, cust?.id, cust?.name, lineUnit]);

  useEffect(() => { api.get("/settings/meal-cards").then((r) => setMealCards(r.data)).catch(() => {}); }, []);

  const openShift = async () => {
    setOpeningBusy(true);
    try {
      const { data } = await api.post("/shifts/open", { opening_amount: Number(openAmount) || 0, pin: openPin, location_id: needLoc ? openLoc : undefined, register_id: openReg || undefined, operator_pin: openOperatorPin || undefined }, { timeout: 30000 });
      setShift(data.shift); setShiftReport(data.report);
      const cs = [makeCart(1)]; setCarts(cs); setActiveId(cs[0].cart_id);
      readyRef.current = true; setOpenAmount(""); setOpenPin(""); setOpenOperatorPin("");
      toast.success("Kasa açıldı");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setOpeningBusy(false); setOpenPin(""); }
  };
  // Radix güvenlik ağı: üst üste kapanan diyaloglarda body'de takılı kalan pointer-events:none temizlenir.
  useEffect(() => {
    if (!closeOpen && !zReport) {
      const t = setTimeout(() => {
        if (typeof document !== "undefined" && document.body.style.pointerEvents === "none") {
          document.body.style.pointerEvents = "";
        }
      }, 450);
      return () => clearTimeout(t);
    }
  }, [closeOpen, zReport]);

  const resetCloseAll = () => {
    setCloseOpen(false); setZReport(null); setShowSupApproval(false); setSupMethod("pin");
    setClosePin(""); setCountedCash("0"); setCloseOperatorPin(""); setSupCred({ pin: "", card: "", email: "", password: "" });
  };

  const closeShift = async () => {
    if (countedCash === "") { toast.error("Sayılan nakit zorunludur — kasadaki fiili nakiti girin."); return; }
    setClosing(true);
    try {
      const body = { pin: closePin, counted_cash: parseFloat(String(countedCash).replace(",", ".")) || 0, operator_pin: closeOperatorPin || undefined };
      if (!can("shift.close.approve")) {
        if (supCred.pin) body.approver_pin = supCred.pin;
        if (supCred.card) body.approver_card = supCred.card;
        if (supCred.email) body.approver_email = supCred.email;
        if (supCred.password) body.approver_password = supCred.password;
      }
      const { data } = await api.post("/shifts/close", body, { timeout: 30000 });
      const _rep = data.report;
      setShift(null); setShiftReport(null); setCarts([]); setActiveId(null);
      setShowSupApproval(false); setSupMethod("pin");
      setClosePin(""); setCountedCash("0"); setSupCred({ pin: "", card: "", email: "", password: "" });
      // TEK diyalog: kapanış formu → Z-rapor adımına geçer (closeOpen açık kalır). İkinci bir Radix
      // dialog AÇILMADIĞI için body'de pointer-events:none takılması / ekran kilitlenmesi İMKANSIZ.
      setZReport(_rep);
      toast.success("Kasa kapatıldı · Z raporu alındı, kaydedildi ve yöneticilere bildirildi");
      if (_rep?.reward_earned && (_rep?.reward_points || 0) > 0) {
        toast.success(`⭐ Tebrikler! Kasa ${_rep.over_short === 0 ? "denk" : "fazla"} çıktı — +${_rep.reward_points} ödül puanı kazandınız!`, { duration: 6000 });
      }
      setTimeout(() => printThermal({ type: "z", report: _rep }), 200);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setClosing(false); setClosePin(""); setSupCred({ pin: "", card: "", email: "", password: "" }); }
  };

  const buildLocalSale = (payload) => {
    const localLines = lines.map((x) => ({ name: x.name, quantity: x.qty, unit_price: lineUnit(x), line_total: round2(x.qty * lineUnit(x)) }));
    const tenders = (payload.payments || []).map((t) => ({ method: t.method, currency: t.currency, amount: t.amount, amount_try: t.amount }));
    const paidTry = round2(tenders.reduce((s, t) => s + (t.currency === "TRY" ? (t.amount || 0) : 0), 0));
    const subtotal = round2(total / 1.2);
    return {
      sale_no: nextOfflineNo(), offline: true,
      location_name: shift?.location_name, created_at: new Date().toISOString(),
      customer_id: payload.customer_id || null, customer_name: cust?.name || null,
      lines: localLines, subtotal, vat_total: round2(total - subtotal),
      discount: round2(custDisc + manualDisc), total,
      change_try: round2(Math.max(0, paidTry - total)),
      payment: { tenders },
    };
  };

  const completeOffline = (payload) => {
    payload = { ...payload, offline: true };  // yönetici görünürlüğü için çevrimdışı-köken bayrağı (senkronda backend'e taşınır)
    const soldId = active?.cart_id;
    let localSale;
    try {
      localSale = buildLocalSale(payload);
      const cnt = enqueueSale({ client_tx_id: payload.client_tx_id, payload, customer_id: payload.customer_id || null, local_no: localSale.sale_no, created_at: localSale.created_at });
      setPendingCount(cnt); setQueueError("");
    } catch {
      setQueueError(QUEUE_STORAGE_ERROR); toast.error("Satış kaydedilmedi; sepet korundu. " + QUEUE_STORAGE_ERROR);
      return;
    }
    const left = carts.filter((c) => c.cart_id !== soldId);
    const next = left.length ? left : [makeCart(1)];
    setCarts(next); setActiveId(next[0].cart_id);
    setPayOpen(false);
    setLastSale(localSale);
    toast.warning(`Çevrimdışı: satış kaydedildi (${localSale.sale_no}) · bağlantı gelince otomatik eşitlenecek`);
    printThermal({ type: "sale", sale: localSale, cashier: user?.name });
  };

  const syncQueue = useCallback(async () => {
    try {
      const q = getQueue();
      setQueueError("");
      if (q.length === 0 || !navigator.onLine) { setPendingCount(q.length); return; }
      setSyncing(true);
      let ok = 0; const failedNos = [];
      for (const item of q) {
        try {
          const { data } = await api.post("/sales", item.payload);
          if (item.customer_id) { try { await api.post(`/e-belge/invoice/${data.id}`, { auto: true }); } catch (e) {} }
          removeSale(item.client_tx_id); ok += 1;
        } catch (err) {
          if (isNetworkError(err)) break; // hâlâ çevrimdışı — kalanı sonraya bırak
          removeSale(item.client_tx_id); failedNos.push(item.local_no); // sunucu reddi (ör. stok) — kuyruktan çıkar, bildir
        }
      }
      setPendingCount(getQueue().length);
      if (ok || failedNos.length) { logSync({ ok, failed: failedNos.length, nos: failedNos }); setSyncLog(getSyncLog()); }
      if (ok) { toast.success(`${ok} çevrimdışı satış eşitlendi`); loadShift(); loadProducts(); loadPolicy(); }
      if (failedNos.length) toast.error(`${failedNos.length} satış eşitlenemedi (ör. stok yetersiz): ${failedNos.join(", ")} — kontrol edin`);
    } catch {
      setQueueError(QUEUE_STORAGE_ERROR); toast.error(QUEUE_STORAGE_ERROR);
    } finally { setSyncing(false); }
  }, [loadShift, loadProducts, loadPolicy]);
  const syncQueueRef = useRef(syncQueue); syncQueueRef.current = syncQueue;

  useEffect(() => {
    try { setPendingCount(queueCount()); } catch { setQueueError(QUEUE_STORAGE_ERROR); }
    setSyncLog(getSyncLog());
    if (!navigator.onLine) setOfflineSince(Date.now());
    else syncQueueRef.current();
  }, []);
  useEffect(() => {
    const goOnline = () => { setOnline(true); setOfflineSince(null); syncQueueRef.current(); };
    const goOffline = () => { setOnline(false); setOfflineSince((s) => s || Date.now()); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);
  useEffect(() => {
    if (online) return;
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, [online]);

  const handlePayment = async (payload) => {
    // Güvenlik ağı: sepet boş/aktif sepet yoksa ödeme diyaloğu takılı kalmasın —
    // "Sepet boş" hatasıyla kilitlenmek yerine diyaloğu temiz kapat.
    if (!active || lines.length === 0) {
      setPayOpen(false); setPayInitial([]);
      api.delete("/shifts/payment").catch(() => {});
      return;
    }
    const salePayload = {
      channel: channel === "toptan" ? "wholesale" : "pos",
      discount_pct: discountPct, discount_token: activeToken?.token || null,
      customer_id: active?.customer_id || null,
      redeem_points: redeemPtsEff || 0,
      wholesale_auth: channel === "toptan" ? (active?.wholesale_auth || null) : null,
      coupon_code: couponInfo ? couponCode.trim().toUpperCase() : null,
      lines: lines.map((x) => ({ product_id: x.product_id, quantity: x.qty, unit_price: lineUnit(x) })),
      client_tx_id: newTxId(),
      ...payload,
    };
    if (!navigator.onLine) { completeOffline(salePayload); return; }
    const soldId = active.cart_id;
    setIssuing(true);
    try {
      const { data } = await api.post("/sales", salePayload, { timeout: 30000 });
      const left = carts.filter((c) => c.cart_id !== soldId);
      const next = left.length ? left : [makeCart(1)];
      const nextActive = next[0].cart_id;
      setCarts(next); setActiveId(nextActive);
      // Satış BAŞARILI → ödeme diyaloğunu HEMEN kapat (arka plan istekleri diyaloğu takmasın).
      setPayOpen(false); setPayInitial([]);
      api.put("/shifts/carts", { carts: next, active_cart_id: nextActive }).catch(() => {});
      api.get("/shifts/current").then((r) => setShiftReport(r.data?.report || null)).catch(() => {});
      loadProducts(); loadPolicy();
      setCouponInfo(null); setCouponCode(""); setRedeemPts(0);
      api.delete("/shifts/payment").catch(() => {});
      cdPost({ mode: "done", store: shift?.location_name || "Groot Market", total: data.total, change: data.change_try || 0, sale_no: data.sale_no });
      toast.success(`Satış tamam: ${data.sale_no} · ${fmtTL(data.total)}`);
      // --- Satış sonrası otomasyon: müşteri seçiliyse e-Fatura/e-Arşiv, değilse fiş; belge otomatik yazdırılır ---
      let edoc = null;
      if (data.customer_id) {
        try {
          const res = await api.post(`/e-belge/invoice/${data.id}`, { auto: true });
          edoc = res.data;
          const stMsg = edoc.status === "sent" ? "gönderildi" : edoc.status === "error" ? "oluşturuldu (gönderilemedi)" : "taslağı oluşturuldu";
          toast.success(`${edoc.doc_type_label} ${stMsg}: ${edoc.doc_no}`);
        } catch (e) {
          toast.error("E-belge kesilemedi, satış fişi yazdırılıyor: " + apiError(e.response?.data?.detail));
        }
      }
      const saleDoc = { ...data, edoc };
      setLastSale(saleDoc);
      let printedInvoice = false;
      if (edoc && data.customer_id && deviceFlag("autoPrintInvoice", true) && policyFlag("invoice_auto_print", shift?.location_id)) {
        setInvoiceDoc(saleDoc);
        setTimeout(() => { printInvoiceNode(); }, 150);
        printedInvoice = true;
      }
      if (!printedInvoice && deviceFlag("autoPrintReceipt", true) && policyFlag("receipt_auto_print", shift?.location_id)) printThermal({ type: "sale", sale: saleDoc, cashier: user?.name });
      if (!edoc && data.customer_id) {
        // Müşterili satışta e-belge kesilemedi → fiş basıldı; sonradan fatura kesimini kilitle (çift vergilendirme önlemi)
        api.post(`/sales/${data.id}/mark-fis-printed`).catch(() => {});
      }
    } catch (err) {
      if (isNetworkError(err)) { completeOffline(salePayload); return; }
      toast.error(apiError(err.response?.data?.detail));
    } finally { setIssuing(false); }
  };

  const reconcileStock = async () => {
    if (!active || lines.length === 0) return true;
    try {
      const { data } = await api.post("/pos/stock-check", { location_id: shift?.location_id, product_ids: lines.map((x) => x.product_id) });
      const stocks = data.stocks || {};
      let adjusted = false; const removed = []; const newLines = [];
      for (const x of lines) {
        const avail = stocks[x.product_id] != null ? stocks[x.product_id] : (x.stock ?? x.qty);
        if (avail <= 0) { adjusted = true; removed.push(x.name); continue; }
        if (x.qty > avail) { adjusted = true; newLines.push({ ...x, qty: avail, stock: avail }); }
        else newLines.push({ ...x, stock: avail });
      }
      if (adjusted) {
        patchActive((c) => ({ ...c, lines: newLines }));
        if (removed.length) toast.error(`Stok tükendi, sepetten çıkarıldı: ${removed.join(", ")}`);
        else toast.warning("Bazı ürün miktarları güncel stoğa göre düşürüldü — kontrol edip tekrar deneyin");
      }
      return !adjusted;
    } catch (e) { return true; }
  };

  const openCheckout = async () => {
    if (lines.length === 0) return;
    const stockOk = await reconcileStock();
    if (!stockOk) return;
    if (channel === "toptan") {
      if (!active?.customer_id) { toast.error("Toptan satış için müşteri seçimi zorunludur."); return; }
      const minAmt = Number(cust?.min_order_amount || 0);
      if (minAmt > 0 && total < minAmt) { toast.error(`Toptan min. tutar ${fmtTL(minAmt)} · sepet ${fmtTL(total)} — limitin altında.`); return; }
    }
    if (discountPct > 0 && !activeToken) { setDiscOpen(true); return; }
    setPayInitial([]);
    if (active) api.put("/shifts/payment", { cart_id: active.cart_id, total, collected: [] }).catch(() => {});
    setPayOpen(true);
  };
  const persistPayment = (collected) => {
    if (!active) return;
    const body = { cart_id: active.cart_id, total, collected };
    api.put("/shifts/payment", body).catch(() => api.put("/shifts/payment", body).catch(() => {}));
  };
  const cancelPayment = () => {
    api.delete("/shifts/payment").catch(() => {});
    setPayInitial([]); setPayOpen(false);
  };
  const onDiscountApplied = (token, by, pct) => {
    if (active) {
      setDiscTokens((m) => ({ ...m, [active.cart_id]: { token, pct, by } }));
      patchActive((c) => ({ ...c, discount: pct }));
    }
    setDiscOpen(false);
  };
  const clearDiscount = () => {
    patchActive((c) => ({ ...c, discount: 0 }));
    if (active) setDiscTokens((m) => { const n = { ...m }; delete n[active.cart_id]; return n; });
  };

  const onApproved = () => {
    if (pending?.type === "item") doRemoveItem(pending.pid);
    else if (pending?.type === "cart") doRemoveCart(pending.id);
    setPending(null);
  };

  const printThermal = useCallback((doc) => {
    setPrintDoc(doc);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintDoc(null), 800);
    }, 80);
  }, []);

  const dismissShiftReminder = () => {
    if (shift?.id) { try { localStorage.setItem("groot_shift_reminder_" + shift.id, "1"); } catch { /* ignore */ } }
    setShiftReminderDismissed(true);
  };
  const shiftCloseReminder = (() => {
    if (!shift?.opened_at || shiftReminderDismissed) return null;
    const opened = new Date(shift.opened_at).getTime();
    if (Number.isNaN(opened)) return null;
    const hoursOpen = (nowTick - opened) / 3600000;
    const late = new Date(nowTick).getHours() >= 21;
    const long = hoursOpen >= 10;
    if (!late && !long) return null;
    return { hoursOpen: Math.floor(hoursOpen), late, long };
  })();

  return (
    <div className="fade-in">
      {queueError && <div role="alert" data-testid="pos-queue-error" className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{queueError}</div>}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Satış Noktası</p><h1 className="font-heading text-2xl font-bold text-slate-900">Kasa / Hızlı Satış</h1></div>
        </div>
        {shift && (
          <div data-testid="pos-mode-badge" className={`inline-flex items-center gap-2 rounded-md px-3.5 py-1.5 text-sm font-semibold ring-1 ring-inset ${channel === "toptan" ? "bg-brand-orange/10 text-brand-orange ring-brand-orange/30" : "bg-brand-green/10 text-brand-green ring-brand-green/30"}`}>
            <Tag className="h-3.5 w-3.5" /> <ChannelBadge channel={channel} />
          </div>
        )}
        <Button data-testid="pos-my-shifts-btn" variant="outline" size="sm"
          onClick={async () => { setMyShiftsOpen(true); setMyShifts(null); try { const { data } = await api.get("/shifts/mine"); setMyShifts(data || []); } catch { setMyShifts([]); } }}
          className="ml-auto shrink-0 border-slate-300 text-slate-600 hover:bg-slate-50">
          <History className="mr-1.5 h-4 w-4" /> Geçmiş Vardiyalarım
        </Button>
      </div>

      <Dialog open={myShiftsOpen} onOpenChange={setMyShiftsOpen}>
        <DialogContent data-testid="my-shifts-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Geçmiş Vardiyalarım</DialogTitle>
            <DialogDescription>Kapattığınız vardiyaların Z-rapor özetleri — yalnız kendi vardiyalarınız.</DialogDescription>
          </DialogHeader>
          {myShifts === null ? (
            <div className="h-24 animate-pulse rounded-md bg-slate-100" />
          ) : myShifts.length === 0 ? (
            <p data-testid="my-shifts-empty" className="py-6 text-center text-sm text-slate-400">Henüz kapanmış vardiyanız yok.</p>
          ) : (
            <div className="space-y-2">
              {myShifts.map((s) => {
                const r = s.report || {};
                const money = (v) => fmtTL(v ?? 0);
                return (
                  <div key={s.id} data-testid={`my-shift-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Clock className="h-4 w-4 text-brand-green" />
                        {new Date(s.closed_at || s.opened_at).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                      {s.register_name && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{s.register_name}</span>}
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] sm:grid-cols-4">
                      <div><span className="text-slate-400">Ciro</span><p className="font-bold tabular text-slate-900">{money(r.grand_total)}</p></div>
                      <div><span className="text-slate-400">Satış</span><p className="font-bold tabular text-slate-700">{r.sales_count ?? 0}</p></div>
                      <div><span className="text-slate-400">Nakit</span><p className="font-bold tabular text-slate-700">{money(r.cash_total)}</p></div>
                      <div><span className="text-slate-400">Kart</span><p className="font-bold tabular text-slate-700">{money(r.card_total)}</p></div>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 text-[11px] text-slate-500">
                      <span>Beklenen çekmece: <b className="tabular text-slate-700">{money(r.expected_drawer)}</b></span>
                      {s.counted_cash != null && <span>Sayılan: <b className="tabular text-slate-700">{money(s.counted_cash)}</b></span>}
                      {r.refund_count ? <span>İade: <b className="tabular text-slate-700">{r.refund_count}</b></span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {!shiftLoaded ? (
        <div className="h-64 animate-pulse rounded-md bg-slate-200/60" />
      ) : !shift ? (
        <div className="mx-auto max-w-md rounded-lg bg-white p-8 text-center ring-1 ring-inset ring-slate-200" data-testid="shift-open-panel">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100"><LockKeyhole className="h-7 w-7 text-slate-400" /></div>
          <h2 className="font-heading text-lg font-bold text-slate-800">Kasanız Kapalı</h2>
          <p className="mt-1 text-sm text-slate-500">Satışa başlamak için açılış tutarı ve PIN kodunuzla kasayı açın.</p>
          <div className="mt-5 space-y-3 text-left">
            {needLoc && (
              <div>
                <label className="overline mb-1 block">Lokasyon (kasa)</label>
                <select data-testid="shift-open-location" value={openLoc} onChange={(e) => { setOpenLoc(e.target.value); setActive(e.target.value); }}
                  className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                  {locOpts.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type_label ? ` (${l.type_label})` : ""}</option>)}
                </select>
              </div>
            )}
            <div data-testid="shift-open-operator" className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
              <span className="text-xs font-medium text-slate-500">Kasayı açan</span>
              <span className="text-sm font-bold text-slate-800" data-testid="shift-open-operator-name">{user?.name}</span>
            </div>
            {registers.length === 1 && (
              <div data-testid="shift-open-register-single" className="flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-inset ring-emerald-200">
                <span className="text-xs font-medium text-emerald-700">Kasa (otomatik seçildi)</span>
                <span className="text-sm font-bold text-emerald-800">{registers[0].name}</span>
              </div>
            )}
            {registers.length > 1 && (
              <div>
                <label className="overline mb-1 block">Kasa (hangi kasadasınız?)</label>
                <select data-testid="shift-open-register" value={openReg} onChange={(e) => setOpenReg(e.target.value)}
                  className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                  {registers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-slate-400">Bulunduğunuz kasayı seçin.</p>
              </div>
            )}
            <div>
              <label className="overline mb-1 block">Açılış Tutarı ({curSym()})</label>
              <Input data-testid="shift-open-amount" type="number" value={openAmount} onChange={(e) => setOpenAmount(e.target.value)} placeholder="0,00" className="tabular text-lg" />
              <p className="mt-1 text-[11px] text-slate-400" data-testid="shift-open-amount-hint">Kasadaki fiili başlangıç nakdini girin. Önceki vardiyadan <b>devir yapılmaz</b> — boş bırakırsanız 0 {curSym()} kabul edilir.</p>
            </div>
            <div>
              <label className="overline mb-1 block">Kasa Şifresi</label>
              <Input data-testid="shift-open-pin" type="password" inputMode="numeric" autoComplete="new-password" value={openPin} onChange={(e) => setOpenPin(e.target.value)} placeholder="••••" className="tabular text-lg tracking-widest" />
              <p className="mt-1 text-[11px] text-slate-400">Kasaya atanan 4 haneli şifreyi girin (yöneticiniz verir). Kasada atanmış şifre yoksa kendi PIN'inizi kullanın.</p>
            </div>
            {user?.is_shared_login && (
              <div className="rounded-md bg-indigo-50 p-3 ring-1 ring-inset ring-indigo-200">
                <label className="overline mb-1 block text-indigo-700">Personel Kodu / PIN <span className="text-red-500">*</span></label>
                <Input data-testid="shift-open-operator-pin" type="password" inputMode="numeric" autoComplete="new-password" value={openOperatorPin} onChange={(e) => setOpenOperatorPin(e.target.value.replace(/\D/g, ""))} placeholder="Kendi PIN'iniz ••••" className="tabular text-lg tracking-widest" />
                <p className="mt-1 text-[11px] text-indigo-500">Ortak giriş — <b>vardiyayı açan personelin kendi PIN'ini</b> girmesi gerekir. Z-raporunda gerçek kişi görünür.</p>
              </div>
            )}
          </div>
          <Button data-testid="shift-open-btn" onClick={openShift} disabled={openingBusy || !openPin || (needLoc && !openLoc) || (registers.length > 0 && !openReg) || (user?.is_shared_login && !openOperatorPin)}
            className="mt-4 h-12 w-full bg-brand-green text-base font-bold text-white hover:bg-brand-green-600">
            <DoorOpen className="mr-2 h-5 w-5" /> Kasayı Aç
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-900 p-3 text-white" data-testid="shift-bar">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1.5 text-emerald-400"><DoorOpen className="h-4 w-4" /> Kasa Açık</span>
              {shift.location_name && <span className="text-slate-300" data-testid="shift-location">Lokasyon: <b className="text-white">{shift.location_name}</b></span>}
              {shift.register_name && <span className="text-slate-300" data-testid="shift-register">Kasa: <b className="text-white">{shift.register_name}</b></span>}
              <span className="text-slate-300"><Clock className="mr-1 inline h-3.5 w-3.5" />{shift.opened_at ? new Date(shift.opened_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
              <span className="text-slate-300">Açılış: <b className="tabular text-white">{fmtTL(shiftReport?.opening_amount ?? 0)}</b></span>
              {shift.operator_name && <span className="text-indigo-300" data-testid="shift-operator">Personel: <b className="text-white">{shift.operator_staff_code ? `${shift.operator_name} · ${shift.operator_staff_code}` : shift.operator_name}</b></span>}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5 text-slate-300" data-testid="shift-live-badge" title="Bu rakamlar anlıktır; Z raporu ancak kasayı kapatıp PIN ile kaydedince kesinleşir.">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> ANLIK
              </span>
              <span className="text-slate-300">Anlık Ciro: <b className="tabular text-white">{fmtTL(shiftReport?.grand_total ?? 0)}</b> ({shiftReport?.sales_count ?? 0})</span>
              <span className="text-slate-300">Anlık Kasa: <b className="tabular text-emerald-400">{fmtTL(shiftReport?.expected_drawer ?? 0)}</b></span>
              {!online && (
                <span data-testid="pos-offline-badge" className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 px-2 py-0.5 font-semibold text-red-300">
                  <WifiOff className="h-3.5 w-3.5" /> ÇEVRİMDIŞI{offlineSince ? ` · ${Math.max(0, Math.floor((Date.now() - offlineSince) / 60000))} dk` : ""}
                </span>
              )}
              {pendingCount > 0 && (
                <span data-testid="pos-pending-sync" className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 px-2 py-0.5 font-semibold text-amber-300">
                  <UploadCloud className="h-3.5 w-3.5" /> Eşitlenecek: {pendingCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {syncLog.length > 0 && (
                <Button data-testid="pos-synclog-btn" onClick={() => setSyncLogOpen(true)} size="sm" variant="outline"
                  className="border-slate-500 bg-transparent text-slate-300 hover:bg-white/10 hover:text-white">
                  <History className="mr-1.5 h-4 w-4" /> Eşitleme Geçmişi
                </Button>
              )}
              {pendingCount > 0 && (
                <Button data-testid="pos-sync-btn" onClick={() => syncQueue()} disabled={!online || syncing} size="sm" variant="outline"
                  className="border-amber-300 bg-transparent text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 disabled:opacity-50">
                  {syncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Şimdi Eşitle ({pendingCount})
                </Button>
              )}
              <Button data-testid="pos-customer-display-btn" onClick={() => window.open("/musteri-ekran?k=" + encodeURIComponent(shift?.id || ""), "groot_customer_display", "width=1100,height=800")} size="sm" variant="outline"
                className="border-sky-300 bg-transparent text-sky-300 hover:bg-sky-500/10 hover:text-sky-200">
                <Monitor className="mr-1.5 h-4 w-4" /> Müşteri Ekranı
              </Button>
              <Button data-testid="pos-checkout-board-btn" onClick={() => window.open("/musteri-ekran?k=genel" + (shift?.location_id ? "&loc=" + encodeURIComponent(shift.location_id) : ""), "groot_checkout_board", "width=1100,height=800")} size="sm" variant="outline"
                className="border-emerald-300 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200">
                <DoorOpen className="mr-1.5 h-4 w-4" /> Sıradaki Kasa
              </Button>
              {scaleSupported() && feature("cap.scale") && (
                <Button data-testid="pos-scale-btn" onClick={async () => { if (scale.connected) return disconnectScale(); try { await connectScale(deviceFlag("scaleBaud", 9600)); toast.success("Tartı bağlandı — kilolu üründe ağırlık canlı okunacak"); } catch (e) { toast.error(e.message || "Tartı bağlanamadı"); } }} size="sm" variant="outline"
                  className={scale.connected ? "border-emerald-300 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200" : "border-slate-300 bg-transparent text-slate-300 hover:bg-slate-500/10 hover:text-slate-100"}>
                  <Scale className="mr-1.5 h-4 w-4" /> {scale.connected ? `Tartı ✓ ${scale.weight.toFixed(3)} kg` : "Tartıya Bağlan"}
                </Button>
              )}
              <Button data-testid="shift-close-btn" onClick={() => setCloseOpen(true)} size="sm" variant="outline"
                className="border-red-300 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200">
                <DoorClosed className="mr-1.5 h-4 w-4" /> Kasayı Kapat (Z Raporu)
              </Button>
            </div>
          </div>

          {shiftCloseReminder && (
            <div data-testid="shift-close-reminder" className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3">
              <Clock className="h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-amber-800">Vardiya Kapanış Hatırlatması</p>
                <p className="text-[13px] text-amber-700">
                  {shiftCloseReminder.long ? `Kasa ${shiftCloseReminder.hoursOpen} saattir açık.` : "Mağaza kapanış saati (21:00) geçti."} Gün sonu <b>Z raporunu</b> almayı unutmayın. Gece satışına devam edecekseniz bu hatırlatmayı kapatabilirsiniz.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="shift-reminder-close-shift" size="sm" onClick={() => setCloseOpen(true)} className="bg-red-500 text-white hover:bg-red-600">
                  <DoorClosed className="mr-1.5 h-4 w-4" /> Kasayı Kapat
                </Button>
                <Button data-testid="shift-reminder-dismiss" size="sm" variant="ghost" onClick={dismissShiftReminder} className="text-amber-700 hover:bg-amber-100">
                  Hatırlatmayı Kapat
                </Button>
              </div>
            </div>
          )}

          {/* Sepet sekmeleri */}
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="cart-tabs">
            {carts.map((c) => {
              const cnt = (c.lines || []).length;
              const isA = c.cart_id === active?.cart_id;
              return (
                <div key={c.cart_id} data-testid={`cart-tab-${c.cart_id}`}
                  className={`group inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold ring-1 ring-inset transition-colors ${isA ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                  <button onClick={() => { setActiveId(c.cart_id); setLastSale(null); }} className="inline-flex items-center gap-1.5">
                    <ShoppingCart className="h-3.5 w-3.5" /> {c.label}
                    <span className={`tabular rounded-full px-1.5 text-[10px] ${isA ? "bg-white/20" : "bg-slate-100"}`}>{cnt}</span>
                  </button>
                  <button data-testid={`cart-tab-close-${c.cart_id}`} onClick={() => requestRemoveCart(c.cart_id)} className={`rounded-sm p-0.5 ${isA ? "hover:bg-white/20" : "hover:bg-slate-200"}`}><X className="h-3 w-3" /></button>
                </div>
              );
            })}
            <button data-testid="cart-add-btn" onClick={addCart} className="inline-flex items-center gap-1 rounded-md bg-brand-orange/10 px-3 py-1.5 text-sm font-semibold text-brand-orange hover:bg-brand-orange/20">
              <Plus className="h-4 w-4" /> Yeni Sepet
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="relative mb-3 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="pos-search" value={q} onChange={(e) => { const v = e.target.value; const mm = v.match(/^(\d{1,3})\s*[xX*]$/); if (mm) { const n = Math.max(1, parseInt(mm[1], 10)); setQtyMult(n); setQ(""); toast.info(`Sonraki ürün ×${n}`); } else { setQ(v); } }} placeholder="Adet+barkod (örn. 5x) · ürün/barkod ara — el terminali de okur" className="pl-9" />
                </div>
                <Button data-testid="scan-barcode-btn" onClick={() => setScanOpen(true)} className="shrink-0 bg-brand-green text-white hover:bg-brand-green-600">
                  <ScanLine className="mr-1.5 h-4 w-4" /> Barkod Tara
                </Button>
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="pos-qty-multiplier">
                <span className="mr-0.5 text-xs font-semibold text-slate-500">Adet:</span>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((n) => (
                  <button key={n} type="button" data-testid={`pos-qty-${n}`} onClick={() => setQtyMult(n)}
                    className={`h-8 min-w-[2.1rem] rounded-md px-2 text-sm font-bold ring-1 ring-inset transition-colors ${qtyMult === n ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:ring-brand-green"}`}>
                    {n}×
                  </button>
                ))}
                <input data-testid="pos-qty-custom" inputMode="numeric" value={qtyMult > 1 ? qtyMult : ""}
                  onChange={(e) => { const n = parseInt(e.target.value.replace(/\D/g, ""), 10); setQtyMult(n > 0 ? n : 1); }}
                  placeholder="özel" className="h-8 w-16 rounded-md border border-input px-2 text-sm tabular" />
              </div>
              {qtyMult > 1 && (
                <div data-testid="pos-qty-active" className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-brand-green/10 px-3 py-2 text-sm font-semibold text-brand-green ring-1 ring-inset ring-brand-green/30">
                  <span>Sonraki ürün <b className="tabular">×{qtyMult}</b> eklenecek — barkod okutun ya da ürüne dokunun</span>
                  <button type="button" data-testid="pos-qty-cancel" onClick={resetMult} className="rounded px-2 py-0.5 hover:bg-brand-green/15">Vazgeç (×1)</button>
                </div>
              )}
              {allCats.length > 0 && (
                <div data-testid="pos-cat-tabs" className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
                  <button type="button" data-testid="pos-cat-all" onClick={() => setSelectedCat("")}
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${selectedCat === "" ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:ring-brand-green"}`}>Tümü</button>
                  {allCats.map((c) => (
                    <button key={c} type="button" data-testid={`pos-cat-${c}`} onClick={() => setSelectedCat(c)}
                      className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${selectedCat === c ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:ring-brand-green"}`}>{c}</button>
                  ))}
                </div>
              )}
              <div ref={gridRef} data-testid="pos-product-grid" className="grid grid-cols-5 gap-2">
                {products.length === 0 && (
                  <div className="col-span-full py-10 text-center text-sm text-slate-400">{q ? `"${q}" için ürün bulunamadı` : "Bu grupta satılabilir ürün yok"}</div>
                )}
                {posPageItems.map((p) => (
                  <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => add(p)} disabled={p.stock_on_hand <= 0}
                    className="flex flex-col overflow-hidden rounded-md bg-white text-left ring-1 ring-inset ring-slate-200 transition-transform hover:ring-brand-green active:scale-[0.98] disabled:opacity-40">
                    <div className="relative h-14 w-full overflow-hidden bg-slate-100">
                      <img src={productImage(p)} alt="" className="h-full w-full object-cover" loading="lazy" />
                      {channel === "market" && p.category_discount_pct > 0 && (
                        <span className="absolute left-0 top-0 rounded-br-md bg-red-500 px-1 py-0.5 text-[9px] font-bold text-white">%{p.category_discount_pct}</span>
                      )}
                    </div>
                    <div className="p-1.5">
                      <p className="line-clamp-2 text-[11px] font-medium leading-tight text-slate-800">{p.name}</p>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="tabular text-xs font-bold text-brand-green">{fmtTL(priceOf(p))}</span>
                        <span className="tabular text-[9px] text-slate-400">{p.stock_on_hand}{p.base_unit}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {posTotalPages > 1 && (
                <div className="mt-3 flex flex-wrap items-center justify-end gap-1" data-testid="pos-pagination">
                  <span className="mr-2 text-[11px] text-slate-400">{products.length} ürün · sayfa {posCurPage}/{posTotalPages}</span>
                  <button type="button" data-testid="pos-page-prev" disabled={posCurPage <= 1} onClick={() => setPosPage((p) => Math.max(1, p - 1))}
                    className="h-8 rounded-md px-2.5 text-sm font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-brand-green disabled:opacity-40">‹</button>
                  {posPageWindow()[0] > 1 && (
                    <button type="button" data-testid="pos-page-1" onClick={() => setPosPage(1)} className="h-8 min-w-[2rem] rounded-md px-2 text-sm font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-brand-green">1</button>
                  )}
                  {posPageWindow()[0] > 2 && <span className="px-1 text-slate-400">…</span>}
                  {posPageWindow().map((n) => (
                    <button key={n} type="button" data-testid={`pos-page-${n}`} onClick={() => setPosPage(n)}
                      className={`h-8 min-w-[2rem] rounded-md px-2 text-sm font-semibold ring-1 ring-inset transition-colors ${posCurPage === n ? "bg-brand-green text-white ring-brand-green" : "text-slate-600 ring-slate-200 hover:ring-brand-green"}`}>{n}</button>
                  ))}
                  {posPageWindow().slice(-1)[0] < posTotalPages - 1 && <span className="px-1 text-slate-400">…</span>}
                  {posPageWindow().slice(-1)[0] < posTotalPages && (
                    <button type="button" data-testid={`pos-page-${posTotalPages}`} onClick={() => setPosPage(posTotalPages)} className="h-8 min-w-[2rem] rounded-md px-2 text-sm font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-brand-green">{posTotalPages}</button>
                  )}
                  <button type="button" data-testid="pos-page-next" disabled={posCurPage >= posTotalPages} onClick={() => setPosPage((p) => Math.min(posTotalPages, p + 1))}
                    className="h-8 rounded-md px-2.5 text-sm font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-brand-green disabled:opacity-40">›</button>
                </div>
              )}
            </div>

            {/* Sepet */}
            <div className="lg:sticky lg:top-6 lg:self-start">
              <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
                <div className="border-b border-slate-100 p-3">
                  <p className="font-heading font-bold text-slate-800">{active?.label} ({lines.length})</p>
                  {can("customer.read") && (
                    <CustomerSearchSelect selected={cust} onSelect={setCustomerC} />
                  )}
                  {cust && <p className="mt-1 text-[11px] text-brand-orange">İskonto: genel %{cust.default_discount_pct || 0}{(cust.category_discounts || []).length ? " + kategori bazlı" : ""}</p>}
                  {cust?.can_wholesale && (
                    active?.wholesale_auth ? (
                      <div data-testid="pos-wholesale-badge" className="mt-1.5 flex items-center justify-between rounded-md bg-brand-orange/10 px-2 py-1 text-[11px] font-semibold text-brand-orange ring-1 ring-inset ring-brand-orange/30">
                        <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> Toptan fiyat yetkili · {active.wholesale_auth.method === "sms" ? "SMS" : "amir onayı"}</span>
                        <button data-testid="pos-wholesale-cancel" onClick={() => patchActive((c) => ({ ...c, wholesale_auth: null }))} className="rounded px-1 hover:bg-brand-orange/10">Kaldır</button>
                      </div>
                    ) : (
                      <button data-testid="pos-wholesale-start" onClick={() => setWhCust(cust)} className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-orange/10 px-2 py-1.5 text-[11px] font-semibold text-brand-orange ring-1 ring-inset ring-brand-orange/30 hover:bg-brand-orange/20">
                        <Tag className="h-3.5 w-3.5" /> Toptan (fırsatlı) fiyata geç — doğrulama gerekir
                      </button>
                    )
                  )}
                </div>
                <div ref={cartListRef} className="max-h-[46vh] overflow-y-auto scroll-smooth">
                  {lines.length === 0 && !lastSale && !issuing && <p className="p-6 text-center text-sm text-slate-400">Sepet boş</p>}
                  {issuing && lines.length === 0 && !lastSale && (
                    <div data-testid="sale-processing" className="p-6 text-center text-sm text-slate-500">
                      <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-brand-green" /> İşleniyor… belge hazırlanıyor
                    </div>
                  )}
                  {lastSale && lines.length === 0 && (
                    <div data-testid="sale-receipt" className="p-6 text-center">
                      <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100"><Check className="h-6 w-6 text-emerald-600" /></div>
                      <p className="font-mono text-sm text-slate-700">{lastSale.sale_no}</p>
                      <p className="tabular text-2xl font-bold text-brand-green">{fmtTL(lastSale.total)}</p>
                      {lastSale.change_try != null && lastSale.change_try > 0 && <p className="text-xs text-slate-500">Para üstü: {fmtTL(lastSale.change_try)}</p>}
                      {lastSale.edoc ? (
                        <p data-testid="sale-edoc-badge" className="mt-1 inline-block rounded-sm bg-brand-green/10 px-2 py-0.5 text-xs font-bold text-brand-green">
                          {lastSale.edoc.doc_type_label} · {lastSale.edoc.doc_no}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-slate-400">Satış fişi</p>
                      )}
                      {lastSale.offline && (
                        <p data-testid="offline-receipt-note" className="mt-1 inline-block rounded-sm bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                          Çevrimdışı satış · bağlantı gelince eşitlenecek
                        </p>
                      )}
                      <p className="mt-1 flex items-center justify-center gap-1 text-xs text-emerald-600"><Printer className="h-3.5 w-3.5" /> Otomatik yazdırıldı</p>
                      <Button data-testid="reprint-receipt-btn" onClick={() => printThermal({ type: "sale", sale: lastSale, cashier: user?.name })}
                        variant="ghost" size="sm" className="mt-2 text-slate-500 hover:text-brand-green">
                        <Printer className="mr-1.5 h-4 w-4" /> Yeniden Yazdır
                      </Button>
                    </div>
                  )}
                  {lines.map((x) => (
                    <div key={x.product_id} data-testid={`cart-item-${x.product_id}`} className="flex items-center gap-2 border-b border-slate-50 p-2.5">
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-800">{x.name}</p><p className="tabular text-[11px] text-slate-400">{fmtTL(lineUnit(x))}{x.weighed ? ` / ${x.unit}` : ""} · {fmtTL(round2(lineUnit(x) * x.qty))}</p></div>
                      <div className="flex items-center gap-1">
                        {x.weighed ? (
                          <div className="flex items-center gap-1" data-testid={`cart-weight-${x.product_id}`} title="Kilolu ürünün ağırlığı sepette elle değiştirilemez">
                            <span className="tabular w-16 rounded-sm bg-slate-100 px-1.5 py-1 text-center text-sm font-semibold text-slate-700">{x.qty}</span>
                            <span className="text-[10px] text-slate-400">{x.unit}</span>
                          </div>
                        ) : (
                          <>
                            <button data-testid={`cart-dec-${x.product_id}`} onClick={() => setQtyC(x.product_id, x.qty - 1)} className="rounded-sm bg-slate-100 p-1 hover:bg-slate-200"><Minus className="h-3 w-3" /></button>
                            <span data-testid={`cart-qty-${x.product_id}`} className="tabular w-7 text-center text-sm font-semibold">{x.qty}</span>
                            <button data-testid={`cart-inc-${x.product_id}`} onClick={() => setQtyC(x.product_id, x.qty + 1)} className="rounded-sm bg-slate-100 p-1 hover:bg-slate-200"><Plus className="h-3 w-3" /></button>
                          </>
                        )}
                        <button data-testid={`cart-remove-${x.product_id}`} onClick={() => requestRemoveItem(x.product_id)} className="ml-1 rounded-sm p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-100 p-3">
                  {lines.length > 0 && (
                    <>
                      {feature("cap.discount") && discPolicy && discPolicy.can_self_apply && (
                        <div data-testid="disc-policy-badge" className="mb-2 rounded-md bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700 ring-1 ring-inset ring-emerald-200">
                          İskonto hakkın (bu hafta): {discPolicy.unlimited ? "Sınırsız (Yönetici)" : `en çok %${discPolicy.max_pct} · kalan ${discPolicy.remaining}/${discPolicy.weekly_limit}`}
                        </div>
                      )}
                      {feature("cap.discount") && (
                      <div className="mb-2">
                        <label className="mb-1 flex items-center gap-1 text-xs text-slate-500"><Percent className="h-3.5 w-3.5" /> İndirim</label>
                        {activeToken ? (
                          <div data-testid="cart-discount-applied" className="flex items-center justify-between rounded-md bg-brand-green/10 px-2.5 py-1.5 ring-1 ring-inset ring-brand-green/30">
                            <span className="text-xs font-semibold text-brand-green">%{activeToken.pct} uygulandı · {activeToken.by?.name || "onaylı"}</span>
                            <button type="button" data-testid="cart-discount-remove" onClick={clearDiscount}
                              className="rounded-md bg-white px-2 py-1 text-[11px] font-bold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50">Kaldır</button>
                          </div>
                        ) : (
                          <button type="button" data-testid="cart-discount-btn" onClick={() => setDiscOpen(true)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-md bg-white px-3 py-2 text-xs font-bold text-slate-600 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-50">
                            <Percent className="h-3.5 w-3.5" /> İndirim Uygula (yetkili onayı)
                          </button>
                        )}
                      </div>
                      )}
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-500"><span>Ara toplam</span><span className="tabular">{fmtTL(gross)}</span></div>
                      {custDisc > 0 && <div className="mb-1 flex items-center justify-between text-xs text-brand-orange"><span>Müşteri iskontosu</span><span className="tabular">−{fmtTL(custDisc)}</span></div>}
                      {manualDisc > 0 && (
                        <div className="mb-1 flex items-center justify-between text-xs text-red-500">
                          <span>İskonto (%{discountPct}){activeToken ? ` · ${activeToken.by?.name || "onaylı"}` : " · onay bekliyor"}</span>
                          <span className="tabular">−{fmtTL(manualDisc)}</span>
                        </div>
                      )}
                      {feature("cap.coupon") && (<div className="mb-2">
                        <label className="mb-1 flex items-center gap-1 text-xs text-slate-500"><Ticket className="h-3.5 w-3.5" /> Kupon Kodu</label>
                        {couponInfo ? (
                          <div data-testid="pos-coupon-applied" className="flex items-center justify-between rounded-md bg-amber-50 px-2.5 py-1.5 ring-1 ring-inset ring-amber-200">
                            <span className="text-xs font-semibold text-amber-700">{couponInfo.code} · −{fmtTL(couponDisc)}</span>
                            <button type="button" data-testid="pos-coupon-remove" onClick={clearCoupon}
                              className="rounded-md bg-white px-2 py-1 text-[11px] font-bold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50">Kaldır</button>
                          </div>
                        ) : (
                          <div className="flex gap-1.5">
                            <input data-testid="pos-coupon-input" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                              placeholder="GC-XXXXXX" className="tabular flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs uppercase focus:outline-none focus:ring-1 focus:ring-amber-400" />
                            <button type="button" data-testid="pos-coupon-apply" onClick={applyCoupon} disabled={couponBusy || !couponCode.trim()}
                              className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600 disabled:opacity-50">Uygula</button>
                          </div>
                        )}
                      </div>)}
                      {couponDisc > 0 && <div className="mb-1 flex items-center justify-between text-xs text-amber-600"><span>Kupon indirimi ({couponInfo?.code})</span><span className="tabular">−{fmtTL(couponDisc)}</span></div>}
                      {campDisc > 0 && <div className="mb-1 flex items-center justify-between text-xs text-brand-green" data-testid="cart-campaign"><span>Kampanya{campPrev?.campaigns_applied?.[0]?.name ? ` (${campPrev.campaigns_applied[0].name}${campPrev.campaigns_applied.length > 1 ? " +" + (campPrev.campaigns_applied.length - 1) : ""})` : ""}</span><span className="tabular">−{fmtTL(campDisc)}</span></div>}
                      {loyEnabled && active?.customer_id && (
                        <div className="mb-1 rounded-md bg-brand-orange/5 px-2 py-1.5 ring-1 ring-inset ring-brand-orange/20" data-testid="cart-loyalty">
                          <div className="flex items-center justify-between text-[11px] text-brand-orange"><span>Sadakat puanı (bakiye {loyInfo?.points_balance || 0})</span><span>+{loyInfo?.earn_estimate || 0} kazanılacak</span></div>
                          {(loyInfo?.points_balance || 0) > 0 && (
                            <div className="mt-1 flex items-center gap-2">
                              <Input data-testid="cart-redeem-input" type="number" min="0" value={redeemPts || ""} onChange={(e) => setRedeemPts(Math.max(0, parseInt(e.target.value) || 0))} placeholder={`Puan kullan (min ${loyInfo?.min_redeem || 0})`} className="h-8 text-xs" />
                              {redeemVal > 0 && <span className="whitespace-nowrap text-xs font-semibold text-brand-green">−{fmtTL(redeemVal)}</span>}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm text-slate-500">Toplam</span>
                    <span data-testid="cart-total" className="tabular font-heading text-2xl font-bold text-[#1e3a8a]">{fmtTL(total)}</span>
                  </div>
                  <Button data-testid="checkout-btn" onClick={openCheckout} disabled={lines.length === 0}
                    className="h-12 w-full bg-brand-orange text-base font-bold text-white hover:bg-orange-600">Ödeme Al</Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} />
      <PaymentDialog open={payOpen} total={total} mealCards={feature("cap.meal_card") ? mealCards : { enabled: false, providers: [] }} canVoid={can("sale.void.approve")} initialCollected={payInitial} onPersist={persistPayment} onCancel={cancelPayment} onClose={() => setPayOpen(false)} onConfirm={handlePayment} />

      <Dialog open={syncLogOpen} onOpenChange={setSyncLogOpen}>
        <DialogContent data-testid="pos-synclog-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><History className="h-5 w-5 text-brand-green" /> Çevrimdışı Eşitleme Geçmişi</DialogTitle>
            <DialogDescription>Son eşitleme işlemleri. Çevrimdışı alınan satışlar bağlantı gelince otomatik gönderilir.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {syncLog.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Henüz eşitleme kaydı yok.</p>}
            {syncLog.map((e, i) => (
              <div key={i} data-testid={`synclog-entry-${i}`} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm ring-1 ring-inset ring-slate-200">
                <span className="text-slate-500">{new Date(e.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                <span className="flex items-center gap-2">
                  {e.ok > 0 && <span className="inline-flex items-center gap-1 font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" /> {e.ok} eşitlendi</span>}
                  {e.failed > 0 && <span className="font-semibold text-red-600">{e.failed} başarısız</span>}
                  {!e.ok && !e.failed && <span className="text-slate-400">değişiklik yok</span>}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <DiscountAuthDialog open={discOpen} gross={gross} isAdmin={user?.role === "admin"}
        onClose={() => setDiscOpen(false)} onApplied={onDiscountApplied} />
      <ApprovalDialog open={approveOpen} onClose={() => { setApproveOpen(false); setPending(null); }}
        permission="sale.void.approve" title="Silme Onayı"
        description="Sepetten ürün/sepet silmek için yetkili (şef/müdür) PIN/şifre onayı gerekiyor."
        onApproved={onApproved} />

      {/* Kasa kapatma — PIN */}
      <Dialog open={closeOpen} onOpenChange={(o) => { if (!o) resetCloseAll(); }}>
        <DialogContent className="max-w-sm" data-testid={zReport ? "z-report-dialog" : "close-shift-dialog"}>
          {zReport ? (
            <ZReportView report={zReport} onDone={resetCloseAll} onPrint={() => printThermal({ type: "z", report: zReport })} />
          ) : (
          <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><DoorClosed className="h-5 w-5 text-red-500" /> Kasayı Kapat & Z Raporu</DialogTitle>
            <DialogDescription>PIN kodunuzu girip <b>"Z Raporunu Kaydet"</b> demeden Kasa KAPANMAZ!</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="close-shift-preview">
              <p className="mb-1 flex items-center justify-between text-[11px] font-medium text-slate-500">
                <span>Anlık Özet (henüz kaydedilmedi)</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">taslak</span>
              </p>
              <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Anlık Ciro</span><b className="tabular">{fmtTL(shiftReport?.grand_total ?? 0)}</b></div>
              <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Kasada Olması Gereken</span><b className="tabular text-brand-green">{fmtTL(shiftReport?.expected_drawer ?? 0)}</b></div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Sayılan Nakit (kasadaki fiili nakit) <span className="font-bold text-red-500">— zorunlu</span></label>
              <div className="relative">
                <span className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-sm font-semibold text-slate-500">{curSym()}</span>
                <Input data-testid="close-shift-counted" inputMode="decimal" value={countedCash} onChange={(e) => setCountedCash(e.target.value)} placeholder="Örn. 1.250,00" className="tabular pl-7" />
              </div>
              {countedCash !== "" && (() => {
                const cc = parseFloat(String(countedCash).replace(",", ".")) || 0;
                const os = +(cc - (shiftReport?.expected_drawer ?? 0)).toFixed(2);
                return (
                  <p data-testid="close-shift-overshort" className={`mt-1 text-xs font-semibold ${os < 0 ? "text-red-600" : os > 0 ? "text-emerald-700" : "text-slate-500"}`}>
                    {os < 0 ? `Kasa AÇIĞI: ${fmtTL(Math.abs(os))}` : os > 0 ? `Kasa FAZLASI: ${fmtTL(os)}` : "Kasa denk ✓"}
                  </p>
                );
              })()}
            </div>
            <Input data-testid="close-shift-pin" type="password" inputMode="numeric" autoComplete="new-password" value={closePin} onChange={(e) => setClosePin(e.target.value)} placeholder="Kasa şifresi / PIN ••••" className="tabular text-lg tracking-widest" />
            {user?.is_shared_login && (
              <div className="rounded-md bg-indigo-50 p-2.5 ring-1 ring-inset ring-indigo-200">
                <label className="mb-1 block text-xs font-medium text-indigo-700">Kapatan Personel Kodu / PIN <span className="text-slate-400">(opsiyonel)</span></label>
                <Input data-testid="close-shift-operator-pin" type="password" inputMode="numeric" autoComplete="new-password" value={closeOperatorPin} onChange={(e) => setCloseOperatorPin(e.target.value.replace(/\D/g, ""))} placeholder="Kapatan kişinin PIN'i ••••" className="tabular tracking-widest" />
              </div>
            )}
            {!can("shift.close.approve") && (
              showSupApproval ? (
                <div className="space-y-2 rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="close-shift-supervisor">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-slate-600">Amir / Üst Onayı (opsiyonel)</p>
                    <button type="button" data-testid="close-shift-sup-hide" onClick={() => { setShowSupApproval(false); setSupCred({ pin: "", card: "", email: "", password: "" }); }}
                      className="text-[11px] font-semibold text-slate-400 hover:text-slate-600">Gizle</button>
                  </div>
                  <div className="flex gap-2">
                    {[["pin", KeyRound, "PIN"], ["password", Lock, "Şifre"], ["card", CreditCard, "Kart"]].map(([id, Icon, label]) => (
                      <button key={id} type="button" data-testid={`close-shift-sup-method-${id}`}
                        onClick={() => { setSupMethod(id); setSupCred({ pin: "", card: "", email: "", password: "" }); }}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${supMethod === id ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        <Icon className="h-3.5 w-3.5" /> {label}
                      </button>
                    ))}
                  </div>
                  {supMethod === "pin" && (
                    <Input data-testid="close-shift-sup-pin" type="password" inputMode="numeric" autoComplete="new-password" value={supCred.pin} onChange={(e) => setSupCred({ ...supCred, pin: e.target.value })} placeholder="Amir PIN ••••" className="tabular text-center tracking-widest" />
                  )}
                  {supMethod === "password" && (
                    <div className="space-y-2">
                      <Input data-testid="close-shift-sup-email" autoComplete="off" value={supCred.email} onChange={(e) => setSupCred({ ...supCred, email: e.target.value })} placeholder="Amir e-posta (opsiyonel)" />
                      <Input data-testid="close-shift-sup-password" type="password" autoComplete="new-password" value={supCred.password} onChange={(e) => setSupCred({ ...supCred, password: e.target.value })} placeholder="Amir şifresi" />
                    </div>
                  )}
                  {supMethod === "card" && (
                    <Input data-testid="close-shift-sup-card" autoComplete="off" value={supCred.card} onChange={(e) => setSupCred({ ...supCred, card: e.target.value })} placeholder="Amir kartını okutun" className="font-mono" />
                  )}
                  <p className="text-[10px] text-slate-400">Amir kimliği girilirse kasiyer PIN'i olmadan da kasa kapanır. Sayılan nakit her durumda zorunludur.</p>
                </div>
              ) : (
                <button type="button" data-testid="close-shift-sup-toggle" onClick={() => setShowSupApproval(true)}
                  className="w-full rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50">
                  + Amir / Üst Onayı Ekle (opsiyonel)
                </button>
              )
            )}
            <Button data-testid="close-shift-confirm" onClick={closeShift} disabled={closing || countedCash === "" || (!closePin && !supCred.pin && !supCred.card && !supCred.password)} className="w-full bg-red-600 text-white hover:bg-red-700">
              {closing ? "Kaydediliyor..." : "PIN Onayla · Z Raporunu Kaydet"}
            </Button>
          </div>
          </>
          )}
        </DialogContent>
      </Dialog>

      <ThermalDoc doc={printDoc} />
      <InvoiceA5Doc sale={invoiceDoc} />

      <WholesaleAuthDialog open={!!whCust} customer={whCust} onClose={() => setWhCust(null)} onAuthorized={onWholesaleAuthorized} />

      <Dialog open={weighOpen} onOpenChange={(o) => { if (!o) { setWeighOpen(false); setWeighProduct(null); } }}>
        <DialogContent className="max-w-xs" data-testid="weigh-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ağırlık Gir (Kg)</DialogTitle>
            <DialogDescription>{weighProduct?.name} · {fmtTL(weighProduct ? priceOf(weighProduct) : 0)} / {weighProduct?.base_unit}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {scaleSupported() ? (
              <div data-testid="scale-bar" className={`flex items-center justify-between rounded-md px-3 py-2 text-xs ring-1 ring-inset ${scale.connected ? "bg-emerald-50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <Scale className="h-4 w-4" />
                  {scale.connected
                    ? <span className="text-emerald-700">Tartı bağlı · {scale.weight.toFixed(3)} kg{scale.stable ? "" : " (ölçülüyor…)"}</span>
                    : <span className="text-slate-500">Tartı bağlı değil</span>}
                </span>
                {scale.connected ? (
                  <button data-testid="scale-disconnect" onClick={() => disconnectScale()}
                    className="rounded-md bg-white px-2 py-1 font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50">Bağlantıyı Kes</button>
                ) : (
                  <button data-testid="scale-connect" onClick={async () => { try { await connectScale(deviceFlag("scaleBaud", 9600)); toast.success("Tartıya bağlanıldı — ağırlık canlı okunacak"); } catch (e) { toast.error(e.message || "Tartı bağlanamadı"); } }}
                    className="rounded-md bg-brand-green px-2 py-1 font-semibold text-white hover:bg-brand-green-600">Tartıya Bağlan</button>
                )}
              </div>
            ) : (
              <p className="rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-400 ring-1 ring-inset ring-slate-200">Canlı tartı için Chrome/Edge (WebSerial) gerekir — manuel girebilirsiniz.</p>
            )}
            <div className="flex items-center gap-2">
              <Input data-testid="weigh-input" autoFocus={!scaleActive} inputMode="decimal" value={weighVal}
                readOnly={scaleActive} disabled={scaleActive}
                onChange={(e) => setWeighVal(e.target.value.replace(/[^0-9.,]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") confirmWeigh(); }}
                placeholder={scaleActive ? "Tartıdan okunuyor…" : "Tartı / Kg gir..."}
                className={`tabular text-center text-lg ${scaleActive ? "bg-emerald-50 font-bold text-emerald-700" : ""}`} />
              <span className="text-sm font-semibold text-slate-500">{weighProduct?.base_unit || "kg"}</span>
            </div>
            {scaleActive
              ? <p data-testid="scale-manual-off" className="text-center text-[11px] text-slate-400">Terazide ağırlık var — canlı okunuyor, elle giriş kapalı. Ürünü kaldırınca elle girebilirsiniz.</p>
              : (scale.connected && <p data-testid="scale-manual-ready" className="text-center text-[11px] text-slate-400">Tartı bağlı — ürünü teraziye koyun (ağırlık canlı dolar) ya da ağırlığı elle girin.</p>)}
            {weighVal && weighProduct && (
              <p className="text-center text-sm text-slate-500">Tutar: <b className="text-brand-green">{fmtTL(round2(priceOf(weighProduct) * (parseFloat(String(weighVal).replace(",", ".")) || 0)))}</b></p>
            )}
            <Button data-testid="weigh-confirm" onClick={confirmWeigh} disabled={scaleActive && !scale.stable} className="w-full bg-brand-green text-white hover:bg-brand-green-600">Sepete Ekle</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ZReportView({ report, onDone, onPrint }) {
  if (!report) return null;
  const Row = ({ label, value, strong, accent }) => (
    <div className={`flex items-center justify-between py-1.5 ${strong ? "border-t border-dashed border-slate-300" : ""}`}>
      <span className={`text-sm ${strong ? "font-semibold text-slate-800" : "text-slate-500"}`}>{label}</span>
      <span className={`tabular text-sm font-bold ${accent || "text-slate-900"}`}>{value}</span>
    </div>
  );
  return (
    <>
        <DialogHeader>
          <div className="mx-auto mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Check className="h-8 w-8 text-emerald-600" />
          </div>
          <DialogTitle className="text-center font-heading text-xl">Kasa Kapatıldı</DialogTitle>
          <p className="text-center text-sm font-bold text-brand-green" data-testid="z-taken-label">Z Raporu Alındı ✓</p>
        </DialogHeader>
        <div className="rounded-md bg-slate-50 p-4">
          <p className="mb-1 text-center font-mono text-[11px] text-slate-400">
            {report.cashier_name} · {report.opened_at ? new Date(report.opened_at).toLocaleString("tr-TR") : ""} → {report.closed_at ? new Date(report.closed_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : ""}
          </p>
          {report.register_name && <p className="mb-2 text-center text-[11px] font-semibold text-slate-600" data-testid="z-register-name">Kasa: {report.register_name}</p>}
          <div className="divide-y divide-slate-100">
            <Row label="Açılış (devir)" value={fmtTL(report.opening_amount)} />
            {report.operator_name && <Row label="Vardiyayı Açan (Personel)" value={report.operator_staff_code ? `${report.operator_name} · ${report.operator_staff_code}` : report.operator_name} accent="text-indigo-700" />}
            {report.closed_operator_name && <Row label="Kapatan (Personel)" value={report.closed_operator_name} accent="text-indigo-700" />}
            <Row label="Satış Adedi" value={report.sales_count} />
            <Row label="Toplam Satış" value={fmtTL(report.grand_total)} />
            {report.refund_count > 0 && <Row label="İade Adedi" value={report.refund_count} accent="text-orange-600" />}
            {report.refund_total > 0 && <Row label="İade Tutarı" value={`-${fmtTL(report.refund_total)}`} accent="text-orange-600" />}
            <Row label="Nakit" value={fmtTL(report.cash_total)} accent="text-emerald-700" />
            <Row label="Kredi Kartı" value={fmtTL(report.card_total)} accent="text-sky-700" />
            <Row label="Verilen İndirim" value={fmtTL(report.discount_total)} accent="text-red-600" />
            <Row label="Kasada Olması Gereken" value={fmtTL(report.expected_drawer)} strong accent="text-brand-green" />
            {report.counted_cash != null && <Row label="Sayılan Nakit" value={fmtTL(report.counted_cash)} />}
            {report.over_short != null && <Row label={report.over_short < 0 ? "Kasa Açığı" : report.over_short > 0 ? "Kasa Fazlası" : "Kasa Farkı"} value={fmtTL(Math.abs(report.over_short))} strong accent={report.over_short < 0 ? "text-red-600" : report.over_short > 0 ? "text-emerald-700" : "text-slate-500"} />}
            {report.reward_enabled && <Row label="Ödül Puanı (kasiyer)" value={report.reward_earned ? `★ +${report.reward_points}` : "—"} accent={report.reward_earned ? "text-amber-600" : "text-slate-400"} />}
          </div>
          {report.by_currency?.length > 0 && (
            <div className="mt-2 border-t border-dashed border-slate-300 pt-2">
              <p className="overline mb-1">Ödeme Para Birimleri</p>
              {report.by_currency.map((c) => (
                <div key={c.currency} className="flex justify-between text-[11px] text-slate-500"><span>{c.currency === "TRY" ? "TL" : c.currency} ({c.count})</span><span className="tabular">{Number(c.total || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {c.currency === "TRY" ? "TL" : c.currency}{c.currency !== "TRY" ? ` ≈ ${fmtTL(c.total_try)}` : ""}</span></div>
              ))}
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-dashed border-slate-300 pt-3" data-testid="z-signatures">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Kasiyer</p>
              <p className="mt-5 border-t border-slate-300 pt-1 text-[11px] font-medium text-slate-600">{report.cashier_name || "—"}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Onaylayan Amir</p>
              <p className="mt-5 border-t border-slate-300 pt-1 text-[11px] font-medium text-slate-600">{report.self_close ? "Kasiyer (kendi)" : (report.approver_name || report.approved_by || "—")}</p>
            </div>
          </div>
        </div>
        <div data-testid="z-saved-note" className="rounded-md bg-emerald-50 px-3 py-2 text-center text-[11px] font-medium leading-relaxed text-emerald-700 ring-1 ring-inset ring-emerald-200">
          ✓ Tüm veriler kaydedildi · Yöneticilere bildirildi · Z raporu otomatik yazdırıldı
        </div>
        <div className="flex gap-2">
          <Button onClick={onPrint} variant="outline" className="flex-1" data-testid="z-print-btn"><Printer className="mr-1.5 h-4 w-4" /> Yeniden Yazdır</Button>
          <Button onClick={onDone} className="flex-1 bg-brand-green text-white hover:bg-brand-green-600" data-testid="z-close-btn">Tamam</Button>
        </div>
    </>
  );
}
